@import './fluid-popover.theme.less';

@import '../../styles/mixins/_color.less';
@import '../../styles/mixins/fluid-fading-animations.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';
@import '../../styles/mixins/fluid-common-components.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-popover {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @popover-tokens: @popover-tokens-legacy;
      @popover();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @popover-tokens: @popover-tokens-lm;
      @popover();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @popover-tokens: @popover-tokens-corp;
      @popover();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @popover-tokens: @popover-tokens-portal;
      @popover();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@popover: {
  @header: @popover-tokens[@header];
  @body: @popover-tokens[@body];
  @arrow-shadow: @popover-tokens[@arrow-shadow];
  @arrow-size: 20px;

  display: inline-flex;
  align-items: center;
  position: relative;
  font-family: @font-family;
  font-size: @popover-tokens[font-size];
  font-weight: @popover-tokens[font-weight];

  // ==== Trigger

  .popover-trigger {
    @button-reset();
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;

    // == "Click-to-Persist" Animation

    &:active {
      & + .popover-container {
        @persistent-animation();
      }
    }

    // == Wrapper for Interactive Trigger Element

    &.as-wrapper {
      cursor: unset;
    }
  }

  // ==== Container

  .popover-container {
    display: block;
    width: max-content; // Sizes Popover to fit content if shorter than `width` @Prop
    line-height: @popover-tokens[line-height];
    will-change: transform;

    // == Visible & Hidden

    &.popover-visible { .fade-in(); }
    &.popover-hidden { .fade-out(); }
    &.popover-inert { display: none; }

    // == Placement: Top/Bottom (Shared)

    &[data-popper-placement^="top"],
    &[data-popper-placement^="bottom"] {
      .popover-arrow {
        width: @arrow-size;
        height: 0;

        &::before,
        &::after {
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    // == Placement: Left/Right (Shared)

    &[data-popper-placement^="left"],
    &[data-popper-placement^="right"] {
      .popover-arrow {
        width: 0;
        height: @arrow-size;

        &::before,
        &::after {
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }

    // == Placement: Top

    .popover-arrow(@placement: top; @inverse-placement: bottom);

    &[data-popper-placement^="top"] {
      .popover-arrow {
        &::before {
          filter: @arrow-shadow;
        }
      }
    }

    // == Placement: Bottom

    .popover-arrow(@placement: bottom; @inverse-placement: top);

    &[data-popper-placement^="bottom"] {
      .popover-header ~ .popover-arrow {
        &::after {
          border-bottom-color: @header[background-color];
        }
      }
    }

    // == Placement: Left

    .popover-arrow(@placement: left; @inverse-placement: right);

    // == Placement: Right

    .popover-arrow(@placement: right; @inverse-placement: left);

    // == Persistent State

    &.persistable {
      &:not(.as-wrapper):not(.persistent) {
        cursor: pointer;
      }
    }

    &.persistent {
      cursor: auto;

      .popover-header,
      .popover-content {
        padding-right: @popover-tokens[padding-right] * 1.6; // Account for dismiss button

        & ~ .popover-content {
          padding-right: @popover-tokens[padding-right]; // Negate padding on content
        }
      }
    }

    // == Popover Used as Wrapper for Interactive Element

    &.as-wrapper {
      cursor: auto;
    }

    // == "Click-to-Persist" Animation

    &:active {
      @persistent-animation();
    }
  }

  // ==== Popover Arrow

  .popover-arrow {
    visibility: hidden;
    position: absolute;

    &::before,
    &::after {
      visibility: visible;
      position: absolute;
      width: 0;
      height: 0;
      content: "";
      border-style: solid;
      border-color: transparent;
      pointer-events: none;
    }

    // == Outer Triangle (Border)

    &::before {
      border-width: (@arrow-size / 2);
    }

    // == Inner Triangle (Fill)

    &::after {
      border-width: (@arrow-size / 2) - 2;
    }
  }

  // ==== Close Icon

  .popover-dismiss {
    position: absolute;
    top: -@popover-tokens[border-width];
    right: -@popover-tokens[border-width];

    .close-button(
      @background-hover: transparent;
      @background-active: transparent;
    );
  }

  // ==== Body Container

  .popover-body {
    background-color: @popover-tokens[background-color];
    border-width: @popover-tokens[border-width];
    border-color: @popover-tokens[border-color];
    border-style: @popover-tokens[border-style];
    border-radius: @popover-tokens[border-radius];
    box-shadow: @popover-tokens[box-shadow];

    .transition(@property: transform; @duration: 100ms); // For "click effect"
  }

  // ==== Header

  .popover-header {
    background-color: @header[background-color];
    padding-top: @popover-tokens[padding-top];
    padding-bottom: @popover-tokens[padding-bottom];
    padding-left: @popover-tokens[padding-left];
    padding-right: @popover-tokens[padding-right];
    font-size: @header[font-size];
    line-height: @header[line-height];
    font-weight: @header[font-weight];
    color: @header[text-color];
    border-top-left-radius: (@popover-tokens[border-radius] - 1px);
    border-top-right-radius: (@popover-tokens[border-radius] - 1px);

    // == Close Icon

    & ~ .popover-dismiss {
      @fill-color: contrast(
        @header[background-color],
        .color-shift(@header[background-color], 1750)[new-color],
        @white,
      );
      @fill-color-hover: contrast(
        @header[background-color],
        .color-shift(@header[background-color], 2000)[new-color],
        @liberty-gray-29,
      );

      .close-button(
        @color-default: @fill-color;
        @color-hover: @fill-color-hover;
        @background-hover: transparent;
        @background-active: transparent;
      );
    }
  }

  // ==== Body Content

  .popover-content {
    padding-top: @popover-tokens[padding-top];
    padding-bottom: @popover-tokens[padding-bottom];
    padding-left: @popover-tokens[padding-left];
    padding-right: @popover-tokens[padding-right];
    overflow-wrap: break-word;
    hyphens: auto;
    background-clip: padding-box;
    font-size: @body[font-size];
    line-height: inherit;
    color: @body[text-color];
  }
};


// ================================================================ //
// -- Utility Detached Ruleset(s)

/**
 * Universal <button> element reset styles
 */
@button-reset: {
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
};

/**
 * Animation triggered when Popover becomes persistent. Enhances
 * usability by providing visual indication of persistent status.
 */
@persistent-animation: {
  &:not(.as-wrapper):not(.persistent) {
    .popover-body {
      transform: scale(1.02, 1.02);
    }
  }
};


// ================================================================ //
// -- Mixins

/**
 * Generates Popover's "speech bubble arrow" styles for all 4
 * Popover placements (i.e. top, right, bottom, left).
 */
.popover-arrow(@placement; @inverse-placement) {
  &[data-popper-placement^="@{placement}"] {
    .popover-arrow {
      @{inverse-placement}: 1px;

      &::before,
      &::after {
        @{placement}: 100%;
        border-@{inverse-placement}-width: 0;
      }

      &::before {
        border-@{placement}-color: @popover-tokens[border-color];
      }

      &::after {
        border-@{placement}-color: @white;
      }
    }
  }
};
